import React, { useState, useEffect, useCallback } from 'react';
import { Route as RouteIcon, MapPin, Clock, Navigation, Calendar, LogIn, LogOut } from 'lucide-react';
import ListPageHeader from '../../components/shared/ListPageHeader';
import api from '../../services/api';
import { toast } from 'sonner';
import { getApiErrorMessage } from '../../utils/apiErrors';

const MyRoutesPage = () => {
  const [loading, setLoading] = useState(true);
  const [timeline, setTimeline] = useState([]);
  const [stats, setStats] = useState({ total_distance_km: 0, total_duration_minutes: 0, visits_count: 0 });
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const fetchRoute = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/staff/self/my-routes', { params: { date: selectedDate } });
      setTimeline(data.timeline || []);
      setStats(data.stats || { total_distance_km: 0, total_duration_minutes: 0, visits_count: 0 });
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to load route data'));
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    fetchRoute();
  }, [fetchRoute]);

  const formatDuration = (minutes) => {
    if (!minutes) return '0m';
    const h = Math.floor(minutes / 60);
    const m = Math.round(minutes % 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '--:--';
    try {
      return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch {
      return '--:--';
    }
  };

  const getEventStyle = (type) => {
    switch (type) {
      case 'SHIFT_START': return { bg: 'bg-emerald-50', border: 'border-emerald-200', dot: 'bg-emerald-500', icon: LogIn, iconColor: 'text-emerald-600', label: 'bg-emerald-100 text-emerald-700' };
      case 'SHIFT_END':   return { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400', icon: LogOut, iconColor: 'text-slate-500', label: 'bg-slate-100 text-slate-600' };
      case 'VISIT_START': return { bg: 'bg-blue-50', border: 'border-blue-200', dot: 'bg-blue-500', icon: MapPin, iconColor: 'text-blue-600', label: 'bg-blue-100 text-blue-700' };
      case 'VISIT_END':   return { bg: 'bg-teal-50', border: 'border-teal-200', dot: 'bg-teal-500', icon: MapPin, iconColor: 'text-teal-600', label: 'bg-teal-100 text-teal-700' };
      default:            return { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400', icon: RouteIcon, iconColor: 'text-slate-500', label: 'bg-slate-100 text-slate-600' };
    }
  };

  const getEventTypeLabel = (type) => {
    switch (type) {
      case 'SHIFT_START': return 'Shift Started';
      case 'SHIFT_END':   return 'Shift Ended';
      case 'VISIT_START': return 'Visit';
      case 'VISIT_END':   return 'Checked Out';
      default:            return type;
    }
  };

  return (
    <div className="h-full flex flex-col" data-testid="my-routes-page">
      <ListPageHeader
        title="My Routes"
        subtitle="Your daily route history and visit timeline"
        stats={[
          { icon: Navigation, label: 'Distance', value: `${(stats.total_distance_km || 0).toFixed(1)} km`, color: 'blue' },
          { icon: Clock, label: 'Duration', value: formatDuration(stats.total_duration_minutes), color: 'teal' },
          { icon: MapPin, label: 'Visits', value: stats.visits_count || 0, color: 'slate' },
        ]}
      />

      {/* Date picker bar */}
      <div className="flex-shrink-0 px-2 pt-2">
        <div className="bg-white rounded-md border border-slate-200/60 shadow-sm p-3">
          <div className="flex items-center gap-3">
            <Calendar className="w-4 h-4 text-slate-400 flex-shrink-0" />
            <input
              type="date"
              value={selectedDate}
              max={new Date().toISOString().split('T')[0]}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-1.5 bg-slate-50 border border-slate-200 rounded-lg text-[13px] text-slate-700 focus:ring-2 focus:ring-teal-500/20 focus:outline-none focus:border-teal-500"
            />
            <span className="text-[12px] text-slate-400">
              {selectedDate === new Date().toISOString().split('T')[0] ? 'Today' : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div className="flex-1 overflow-auto px-2 pb-2 pt-2">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex flex-col items-center gap-3">
              <div className="w-8 h-8 border-[3px] border-slate-200 border-t-teal-600 rounded-full animate-spin"></div>
              <p className="text-[13px] text-slate-500">Loading route...</p>
            </div>
          </div>
        ) : timeline.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 bg-white rounded-md border border-slate-200/60 shadow-sm">
            <div className="w-14 h-14 bg-slate-100 rounded-xl flex items-center justify-center mb-4">
              <RouteIcon className="w-7 h-7 text-slate-400" />
            </div>
            <p className="text-[15px] font-medium text-slate-600">No route recorded</p>
            <p className="text-[13px] text-slate-400 mt-1">No shift or visits found for this date</p>
          </div>
        ) : (
          <div className="bg-white rounded-md border border-slate-200/60 shadow-sm overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-100">
              <h3 className="text-[13px] font-semibold text-slate-700">Route Timeline</h3>
            </div>
            <div className="p-4">
              <div className="relative">
                {/* Vertical connector line */}
                <div className="absolute left-[19px] top-6 bottom-6 w-0.5 bg-slate-200"></div>

                <div className="space-y-3">
                  {timeline.map((event, index) => {
                    const style = getEventStyle(event.type);
                    const Icon = style.icon;
                    return (
                      <div
                        key={event.id || index}
                        className={`relative flex gap-3 p-3 rounded-lg border ${style.bg} ${style.border}`}
                      >
                        {/* Dot on the connector line */}
                        <div className="relative z-10 flex-shrink-0 w-10 h-10 rounded-full bg-white border-2 border-slate-200 flex items-center justify-center shadow-sm">
                          <Icon className={`w-4 h-4 ${style.iconColor}`} />
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-medium ${style.label}`}>
                                  {getEventTypeLabel(event.type)}
                                </span>
                                {event.duration_minutes != null && (
                                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 rounded text-[10px] text-slate-600">
                                    <Clock className="w-3 h-3" />
                                    {event.duration_minutes} min
                                  </span>
                                )}
                              </div>
                              <p className="text-[13px] font-medium text-slate-800 mt-1 truncate">
                                {event.location_name || 'Unknown'}
                              </p>
                              {event.address && (
                                <p className="text-[11px] text-slate-400 mt-0.5 truncate">{event.address}</p>
                              )}
                            </div>
                            <span className="text-[12px] text-slate-500 flex-shrink-0 font-medium">
                              {formatTime(event.timestamp)}
                            </span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyRoutesPage;
